import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import { useToast, Toast, ToastType } from '../context/ToastContext';

const ToastContainer: React.FC = () => {
  const { toasts, removeToast, pauseToast, resumeToast } = useToast();

  const getToastIcon = (type: ToastType) => {
    const iconClass = 'w-5 h-5';
    switch (type) {
      case 'success':
        return <CheckCircleIcon className={`${iconClass} text-green-500`} />;
      case 'error':
        return <XCircleIcon className={`${iconClass} text-red-500`} />;
      case 'warning':
        return (
          <ExclamationCircleIcon className={`${iconClass} text-yellow-500`} />
        );
      case 'info':
      default:
        return (
          <InformationCircleIcon className={`${iconClass} text-blue-500`} />
        );
    }
  };

  const getToastStyles = (type: ToastType) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 dark:bg-green-900 border-green-200 dark:border-green-700 text-green-800 dark:text-green-200';
      case 'error':
        return 'bg-red-50 dark:bg-red-900 border-red-200 dark:border-red-700 text-red-800 dark:text-red-200';
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-900 border-yellow-200 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200';
      case 'info':
      default:
        return 'bg-blue-50 dark:bg-blue-900 border-blue-200 dark:border-blue-700 text-blue-800 dark:text-blue-200';
    }
  };

  if (toasts.length === 0) return null;

  return (
    <div className='fixed bottom-4 right-4 z-50 space-y-2 max-w-sm'>
      {toasts.map((toast: Toast) => (
        <div
          key={toast.id}
          className={`flex items-start gap-3 p-4 rounded-lg border shadow-lg ${getToastStyles(toast.type)} animate-slide-in-right`}
          onMouseEnter={() => pauseToast(toast.id)}
          onMouseLeave={() => resumeToast(toast.id)}
        >
          <div className='flex-shrink-0 mt-0.5'>{getToastIcon(toast.type)}</div>
          <div className='flex-1 min-w-0'>
            <p className='text-sm font-semibold mb-1'>{toast.title}</p>
            {toast.message && (
              <p className='text-xs opacity-90 break-words'>{toast.message}</p>
            )}
          </div>
          <button
            onClick={() => removeToast(toast.id)}
            className='flex-shrink-0 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors'
            aria-label='Close toast'
          >
            <XMarkIcon className='w-4 h-4' />
          </button>
        </div>
      ))}
    </div>
  );
};

export default ToastContainer;
